import { useRef, useEffect, useState } from 'react'
import { gsap } from 'gsap'
import { useTheme } from '../../context/ThemeContext'
import './index.scss'

// ─────────────────────────────────────────────────────────────
// "The Survey" — the topographic background plots itself.
//
// topo-bg.svg is fetched once and inlined so each of its ~190
// contour paths can be drawn as a real stroke:
//   · contours draw widest-first (basin lines → nested peaks)
//   · the longest ridge lines carry a glowing plotter head that
//     rides the draw front (dash-window trick, no SVG filters)
//   · lines draw in bright "wet ink", then dry to the theme color
//   · the map holds luminous while plotting, then recedes to ambient
//
// GSAP animates geometry only (dash offsets + timing). All color
// and opacity live in CSS custom properties (--topo-*), so theme
// switches — even mid-plot — recolor instantly with no JS.
// If anything fails (fetch, parser, jsdom) we fall back to the
// plain <img> background with the legacy filter-based theming.
// ─────────────────────────────────────────────────────────────

const PLOT = {
  delay: 1.35, // hold while the Pacman loader exits
  spread: 6.2, // s — window over which contour draws begin
  drawMax: 3.2, // s — draw time of the longest contour
  drawMin: 0.7, // s — draw time of the shortest
  settleLag: 1.5, // s — let the last ink→line settle finish
  heads: 24, // plotter heads on the N longest contours
}

// Fallback <img> theming (legacy filter approach)
const IMG_THEME = {
  dark: { filter: 'saturate(1.1) brightness(1) sepia(0.08)', opacity: 0.45 },
  light: {
    filter: 'hue-rotate(8deg) saturate(0.7) brightness(0.38) contrast(1.2)',
    opacity: 0.75,
  },
  ocean: {
    filter: 'hue-rotate(162deg) saturate(1.4) brightness(0.85)',
    opacity: 0.45,
  },
}

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// The source artwork's full-canvas background rect — a frame, not terrain
const isFrameRect = (d) => /^M0[ ,]0h2048v1117H0V0z$/i.test(d.trim())

const Background = () => {
  const wrapRef = useRef(null)
  const parRef = useRef(null)
  const hostRef = useRef(null)
  const imgRef = useRef(null)
  const [mode, setMode] = useState('svg')
  const { theme } = useTheme()

  // ── Inline the SVG and run the plot ──
  useEffect(() => {
    if (mode !== 'svg') return
    const host = hostRef.current
    if (!host) return

    let alive = true
    const ctrl = new AbortController()
    let tl = null

    ;(async () => {
      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/topo-bg.svg`, {
          signal: ctrl.signal,
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const text = await res.text()
        if (!alive) return

        const doc = new DOMParser().parseFromString(text, 'image/svg+xml')
        if (doc.querySelector('parsererror')) throw new Error('SVG parse error')

        const svg = document.importNode(doc.documentElement, true)
        svg.querySelectorAll('style, defs').forEach((n) => n.remove())
        svg.setAttribute('focusable', 'false')

        const paths = [...svg.querySelectorAll('path')].filter((p) => {
          if (isFrameRect(p.getAttribute('d') || '')) {
            p.remove()
            return false
          }
          ;['fill', 'stroke', 'style', 'class'].forEach((a) =>
            p.removeAttribute(a)
          )
          return true
        })

        host.replaceChildren(svg)

        // Reduced motion: present the finished map, skip the plot
        if (prefersReducedMotion()) {
          host.classList.add('is-ready')
          return
        }

        // Measure while the host is still at opacity 0 — getBBox/getTotalLength
        // work on hidden-by-opacity elements, and nothing paints this tick
        const items = paths
          .map((el) => {
            const len = el.getTotalLength()
            const box = el.getBBox()
            return { el, len, area: box.width * box.height }
          })
          .filter((it) => it.len > 1)

        // Elevation flood: wide basin contours first, nested peaks last
        items.sort((a, b) => b.area - a.area)

        // Index contours — every 5th line reads heavier, like a real map
        items.forEach((it, i) => {
          if (i % 5 === 0) it.el.classList.add('topo-major')
        })

        // Plotter heads ride the longest ridge lines
        const headSet = new Set(
          [...items].sort((a, b) => b.len - a.len).slice(0, PLOT.heads)
        )
        const headsG = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'g'
        )
        headsG.setAttribute('class', 'topo-heads')
        svg.appendChild(headsG)

        const maxLen = items.reduce((m, it) => Math.max(m, it.len), 1)
        const lastIdx = Math.max(1, items.length - 1)
        let lastEnd = 0

        tl = gsap.timeline({ delay: PLOT.delay })

        items.forEach((it, i) => {
          // Slightly super-linear spread: starts sparse, finale denser
          const at = Math.pow(i / lastIdx, 1.12) * PLOT.spread
          const dur = gsap.utils.clamp(
            PLOT.drawMin,
            PLOT.drawMax,
            (it.len / maxLen) * (PLOT.drawMax + 0.4)
          )
          lastEnd = Math.max(lastEnd, at + dur)

          it.el.classList.add('ink')
          gsap.set(it.el, { strokeDasharray: it.len, strokeDashoffset: it.len })
          tl.to(
            it.el,
            { strokeDashoffset: 0, duration: dur, ease: 'power1.inOut' },
            at
          )
          // Wet ink dries — CSS transition eases stroke back to --topo-line
          tl.call(() => it.el.classList.remove('ink'), null, at + dur)

          if (headSet.has(it)) {
            const head = gsap.utils.clamp(40, 110, it.len * 0.18)
            const core = it.el.cloneNode()
            const bloom = it.el.cloneNode()
            core.setAttribute('class', 'topo-head-core')
            bloom.setAttribute('class', 'topo-head-bloom')
            headsG.append(bloom, core)
            // Dash window [drawn, drawn+head): the pen leads the ink and
            // slides off the path's end exactly when its line completes
            gsap.set([bloom, core], {
              strokeDasharray: `${head} ${it.len}`,
              strokeDashoffset: it.len + head,
            })
            tl.to(
              [bloom, core],
              { strokeDashoffset: head, duration: dur, ease: 'power1.inOut' },
              at
            )
          }
        })

        // Light up (CSS fade) and plot
        host.classList.add('is-ready', 'is-plotting')

        // After the last line dries: recede to ambient, drop plot scaffolding
        tl.call(
          () => {
            host.classList.remove('is-plotting')
            headsG.remove()
            items.forEach((it) => {
              it.el.style.removeProperty('stroke-dasharray')
              it.el.style.removeProperty('stroke-dashoffset')
            })
          },
          null,
          lastEnd + PLOT.settleLag
        )
      } catch (e) {
        if (alive) setMode('img')
      }
    })()

    return () => {
      alive = false
      ctrl.abort()
      if (tl) tl.kill()
    }
  }, [mode])

  // ── Gentle pointer parallax (fine pointers, motion allowed) ──
  useEffect(() => {
    if (prefersReducedMotion() || !window.matchMedia('(pointer: fine)').matches)
      return
    const par = parRef.current
    if (!par) return

    const toX = gsap.quickTo(par, 'x', { duration: 1.4, ease: 'power3.out' })
    const toY = gsap.quickTo(par, 'y', { duration: 1.4, ease: 'power3.out' })
    const onMove = (e) => {
      toX((e.clientX / window.innerWidth - 0.5) * 14)
      toY((e.clientY / window.innerHeight - 0.5) * 10)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      gsap.killTweensOf(par)
    }
  }, [])

  // ── Fallback <img> keeps the legacy filter-based theming ──
  useEffect(() => {
    if (mode !== 'img') return
    const cfg = IMG_THEME[theme] ?? IMG_THEME.dark
    if (!wrapRef.current || !imgRef.current) return
    gsap.to(wrapRef.current, {
      filter: cfg.filter,
      duration: 0.9,
      ease: 'power2.inOut',
    })
    gsap.to(imgRef.current, {
      opacity: cfg.opacity,
      duration: 0.9,
      ease: 'power2.inOut',
    })
  }, [theme, mode])

  return (
    <div className="bg-wrap" ref={wrapRef} aria-hidden="true">
      <div className="bg-parallax" ref={parRef}>
        <div className="bg-drift">
          {mode === 'svg' ? (
            <div className="bg-svg-host" ref={hostRef} />
          ) : (
            <img
              ref={imgRef}
              className="bg-img"
              src={`${process.env.PUBLIC_URL}/topo-bg.svg`}
              alt=""
              role="presentation"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Background
